import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WAR_ROOM_RULES,
  WAR_ROOM_SESSION_STATUS_MAP,
  minutesLeft,
  sessionProgress,
  type WarRoomSessionView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { dateTime, plural, WORDS } from '../../lib/format';
import { useCountUp } from '../../lib/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, Modal } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { useToast } from '../../components/Toast';

/**
 * جلسة التركيز.
 *
 * غرفة العمليات لوحةٌ تُقرأ، والجلسة تحوّلها إلى عمل موقوت: ما يُصفّى، ومن
 * يصفّيه، وإلى متى. والفرق ليس في البيانات — الإشارات هي هي — بل في أن
 * أحدًا صار مسؤولًا عنها الآن.
 *
 * **ووضع التركيز يتبع التقدّم لا الحالة.** كلما قلّ المتبقّي خفّت حدّة اللون،
 * وعند الصفر تتبدّل الحالة إلى نجاح. اللونُ الثابت طوال الجلسة يُعلَّم
 * بتجاهله في الدقيقة الخامسة؛ واللون الذي يهدأ يقول «اقتربت» بلا كلمات.
 */

export function SessionBar({
  onScopeChange,
  onActiveChange,
}: {
  onScopeChange?: (rules: string[]) => void;
  onActiveChange?: (active: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const toast = useToast();

  const canManage = atLeast('MANAGER');

  const sessions = useQuery({
    queryKey: ['war-room', 'sessions'],
    queryFn: () => api<WarRoomSessionView[]>('/crm/war-room/sessions?openOnly=true'),
    /*
     * إعادة القراءة كل نصف دقيقة أثناء الجلسة.
     *
     * التقدّم يتغيّر بفعل الفريق كله لا بفعل من يفتح الشاشة، فلوحةٌ لا
     * تُحدَّث تُظهر لكل واحد رقمًا يخصّ لحظة دخوله وحدها.
     */
    refetchInterval: 30_000,
  });

  const active = sessions.data?.find((s) => s.status === 'ACTIVE') ?? null;
  const scheduled = sessions.data?.filter((s) => s.status === 'SCHEDULED') ?? [];

  /* نطاق الجلسة يُصفّي اللوحة تحتها: ما يُطلب تصفيته هو ما يُعرض. */
  const scopeKey = (active?.rules ?? []).join(',');
  useEffect(() => {
    onScopeChange?.(scopeKey ? scopeKey.split(',') : []);
  }, [scopeKey, onScopeChange]);

  /* الرأسُ يحتاج أن يعرف: أثمّة جلسةٌ جارية؟ فلا يعرض «جلسة تركيز» فوق جلسة. */
  useEffect(() => {
    onActiveChange?.(active !== null);
  }, [active, onActiveChange]);

  const act = useMutation({
    mutationFn: (args: { id: string; action: 'start' | 'end' | 'cancel' }) =>
      api(`/crm/war-room/sessions/${args.id}/${args.action}`, { method: 'POST', body: {} }),
    onSuccess: async (_d, args) => {
      await queryClient.invalidateQueries({ queryKey: ['war-room'] });
      toast(
        args.action === 'start'
          ? 'بدأت الجلسة وأُشعر المدعوّون'
          : args.action === 'end'
            ? 'انتهت الجلسة'
            : 'أُلغيت الجلسة',
      );
    },
  });

  /* تسجيل الحضور مرة واحدة عند فتح الغرفة أثناء جلسة جارية. */
  const joined = useMutation({
    mutationFn: (id: string) =>
      api(`/crm/war-room/sessions/${id}/join`, { method: 'POST', body: {} }),
  });

  useEffect(() => {
    if (active) joined.mutate(active.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- مرة لكل جلسة لا لكل تصيير.
  }, [active?.id]);

  return (
    <>
      {active && (
        <ActivePanel
          session={active}
          onEnd={() => act.mutate({ id: active.id, action: 'end' })}
          pending={act.isPending}
          canManage={canManage}
        />
      )}

      {scheduled.length > 0 && (
        <div className="toolbar">
          {scheduled.map((s) => (
            <div key={s.id} className="row" style={{ gap: 'var(--sp-2)' }}>
              <Badge kind={WAR_ROOM_SESSION_STATUS_MAP[s.status].kind}>
                {WAR_ROOM_SESSION_STATUS_MAP[s.status].label}
              </Badge>
              <span className="small">{s.title}</span>
              <span className="tiny mute">{dateTime(s.startsAt)}</span>
              {canManage && (
                <>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: s.id, action: 'start' })}
                  >
                    <Icon name="zap" size={14} />
                    ابدأ الآن
                  </button>
                  <button
                    className="btn btn-sm"
                    disabled={act.isPending}
                    onClick={() => act.mutate({ id: s.id, action: 'cancel' })}
                  >
                    إلغاء
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {act.isError && (
        <div className="alert alert-danger mb-4">
          {act.error instanceof Error ? act.error.message : 'تعذّر تنفيذ الإجراء.'}
        </div>
      )}
    </>
  );
}

/* ==========================================================================
   لوحة الجلسة الجارية
   ========================================================================== */

function ActivePanel({
  session,
  onEnd,
  pending,
  canManage,
}: {
  session: WarRoomSessionView;
  onEnd: () => void;
  pending: boolean;
  canManage: boolean;
}) {
  const { byId } = useTeamDirectory();
  const progress = sessionProgress(session);
  const done = useCountUp(session.done);
  const remaining = useCountUp(session.remaining);

  /*
   * الوقت يُعاد حسابه كل دقيقة لا مع البيانات.
   *
   * «بقيت ٤٠ دقيقة» تصير كذبة بعد عشر دقائق إن لم تتحرّك، ومن يقرأها يبني
   * عليها ترتيب ما تبقّى من عمله.
   */
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const left = minutesLeft(session.endsAt);
  const finished = session.remaining === 0;
  /* النطاقُ بأسمائه: قاعدةٌ أو أكثر، وفارغُه كلُّ القواعد. */
  const ruleLabel = session.rules.length
    ? session.rules.map((k) => WAR_ROOM_RULES.find((r) => r.key === k)?.label ?? k).join(' · ')
    : 'كل القواعد';

  return (
    <section
      className={`focus-panel${finished ? ' focus-done' : ''}`}
      /*
       * حدّة اللون تتبع ما تبقّى.
       * `--focus-heat` من واحد (البداية) إلى صفر (الانتهاء)، وتقرؤها CSS
       * في الشفافية والتوهّج — فتهدأ الشاشة كلما اقترب الفريق.
       */
      style={{
        ['--focus-heat' as string]: session.targetCount
          ? session.remaining / session.targetCount
          : 0,
      }}
      aria-label="جلسة تركيز جارية"
    >
      <div className="focus-head">
        <span className="focus-badge">
          <Icon name={finished ? 'check' : 'zap'} size={16} />
          {finished ? 'اكتملت' : 'جلسة جارية'}
        </span>
        <h2 className="focus-title">{session.title}</h2>
        <span className="tiny mute">{ruleLabel}</span>

        {canManage && (
          <button
            className="btn btn-sm"
            style={{ marginInlineStart: 'auto' }}
            onClick={onEnd}
            disabled={pending}
          >
            إنهاء الجلسة
          </button>
        )}
      </div>

      <div className="focus-metrics">
        <div className="focus-metric">
          <span className="focus-metric-value num">{done}</span>
          <span className="focus-metric-label">أُنجز</span>
        </div>
        <div className="focus-metric">
          <span className="focus-metric-value num">{remaining}</span>
          <span className="focus-metric-label">متبقٍ</span>
        </div>
        <div className="focus-metric">
          <span className="focus-metric-value num">{session.targetCount}</span>
          <span className="focus-metric-label">المطلوب</span>
        </div>
        <div className="focus-metric">
          <span className={`focus-metric-value num${left < 0 ? ' neg' : ''}`}>
            {Math.abs(left)}
          </span>
          <span className="focus-metric-label">{left < 0 ? 'دقيقة تجاوزًا' : 'دقيقة متبقية'}</span>
        </div>
      </div>

      <div
        className="focus-track"
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="focus-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="focus-foot">
        <span className="tiny">{progress}٪</span>

        {/*
          ما ظهر أثناء الجلسة يُعلَن ولا يُخفى.
          جلسةٌ يزيد فيها العمل حقيقةٌ تخصّ من يديرها، وإخفاؤها يجعل
          «صفر منجَز» يبدو تقصيرًا وهو ليس كذلك.
        */}
        {session.appeared > 0 && (
          <span className="tiny" style={{ color: 'var(--warning)' }}>
            ظهرت {plural(session.appeared, WORDS.signal)} أثناء الجلسة
          </span>
        )}

        <div className="focus-people">
          {session.participants.map((p) => {
            const member = byId.get(p.userId);
            return (
              <span
                key={p.userId}
                className={p.joinedAt ? '' : 'focus-absent'}
                title={`${member?.name ?? 'عضو'}${p.joinedAt ? '' : ' — لم يفتح الغرفة بعد'}`}
              >
                <Avatar name={member?.name ?? '؟'} src={member?.avatarUrl} size={24} />
              </span>
            );
          })}
        </div>
      </div>

      {finished && (
        <div className="focus-success">
          <Icon name="check" size={18} />
          <span>صُفّيت الإشارات كلها. أنهِ الجلسة لتُحفظ النتيجة.</span>
        </div>
      )}
    </section>
  );
}

/* ==========================================================================
   جدولة جلسة
   ========================================================================== */

/** الوقت المحلي بصيغة `datetime-local`. */
function localValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** نافذةُ الجدولة — يفتحها زرٌّ في رأس الغرفة، فتُصدَّر لا تُحبس هنا. */
export function SessionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { assignable } = useTeamDirectory();

  const now = new Date();
  const [title, setTitle] = useState('تصفية إشارات اليوم');
  /* النطاقُ اختيارٌ متعدّد: فارغُه كلُّ القواعد المفعَّلة. */
  const [rules, setRules] = useState<Set<string>>(new Set());
  const [startsAt, setStartsAt] = useState(localValue(now));
  const [endsAt, setEndsAt] = useState(localValue(new Date(now.getTime() + 60 * 60_000)));
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const create = useMutation({
    mutationFn: () =>
      api('/crm/war-room/sessions', {
        method: 'POST',
        body: {
          title: title.trim(),
          rules: [...rules],
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          participantIds: [...picked],
        },
      }),
    onSuccess: onCreated,
  });

  const valid = title.trim().length >= 2 && picked.size > 0;

  return (
    <Modal
      title="جلسة تركيز"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'جارٍ الجدولة' : 'جدولة'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّرت الجدولة.'}
        </div>
      )}

      <div className="col">
        <div className="field">
          <label className="label req" htmlFor="s-title">
            عنوان الجلسة
          </label>
          <input
            id="s-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <span className="label">النطاق</span>
          <span className="tiny mute">
            اختر قاعدةً أو أكثر. بلا اختيارٍ تشمل الجلسةُ كلَّ القواعد المفعَّلة، والنطاقُ يُصفّي
            اللوحة تحت الجلسة.
          </span>

          {/*
            اختيارٌ متعدّد بمربّعاتٍ لا بقائمة `multiple`: القائمةُ المتعدّدة
            تحتاج Ctrl للجمع ولا يعرفها أحد، والمربّعاتُ تُقرأ وتُنقر.
          */}
          <div className="scope-pick mt-2">
            <button
              type="button"
              className={`pick-row${rules.size === 0 ? ' pick-row-current' : ''}`}
              onClick={() => setRules(new Set())}
            >
              <span className="pick-row-text">
                <strong>كل القواعد المفعَّلة</strong>
              </span>
              {rules.size === 0 && <Icon name="check" size={16} />}
            </button>
            {WAR_ROOM_RULES.map((r) => {
              const on = rules.has(r.key);
              return (
                <label key={r.key} className={`pick-row${on ? ' pick-row-current' : ''}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => {
                      const next = new Set(rules);
                      if (on) next.delete(r.key);
                      else next.add(r.key);
                      setRules(next);
                    }}
                  />
                  <span className="pick-row-text">
                    <strong>{r.label}</strong>
                  </span>
                </label>
              );
            })}
          </div>
          {rules.size > 0 && (
            <span className="tiny" style={{ color: 'var(--brand-700)' }}>
              {rules.size} من {WAR_ROOM_RULES.length}
            </span>
          )}
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="label req" htmlFor="s-start">
              تبدأ
            </label>
            <input
              id="s-start"
              className="input"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label req" htmlFor="s-end">
              تنتهي
            </label>
            <input
              id="s-end"
              className="input"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <span className="label req">المدعوّون</span>
          <span className="tiny mute">
            يُشعَرون عند بدء الجلسة لا عند جدولتها — الإشعار قبل ساعات يُنسى.
          </span>

          <div className="pick-list mt-2">
            {assignable.map((m) => {
              const on = picked.has(m.userId);
              return (
                <button
                  key={m.userId}
                  type="button"
                  className={`pick-row${on ? ' pick-row-current' : ''}`}
                  onClick={() => {
                    const next = new Set(picked);
                    if (on) next.delete(m.userId);
                    else next.add(m.userId);
                    setPicked(next);
                  }}
                >
                  <Avatar name={m.name} src={m.avatarUrl} size={28} />
                  <span className="pick-row-text">
                    <strong>{m.name}</strong>
                    <small>{m.jobTitle ?? m.email}</small>
                  </span>
                  {on && <Icon name="check" size={16} />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
